import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuthStore } from '@/stores/auth-store'
import { triggerRevalidation } from '@/lib/utils'

export interface TenantFaq {
  id: string
  tenant_id: string
  question: string
  answer: string
  category: string | null
  display_order: number
  is_published: boolean
  created_at: string
  updated_at: string
}

export interface CreateFaqInput {
  question: string
  answer: string
  category?: string | null
  display_order?: number
  is_published?: boolean
}

export interface UpdateFaqInput {
  id: string
  question?: string
  answer?: string
  category?: string | null
  display_order?: number
  is_published?: boolean
}

export function useFAQs() {
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useQuery({
    queryKey: ['tenant_faqs', tenantId],
    enabled: !!tenantId,
    queryFn: async () => {
      const { data, error } = await supabase
        .from('tenant_faqs')
        .select('*')
        .eq('tenant_id', tenantId!)
        .order('category', { ascending: true, nullsFirst: false })
        .order('display_order', { ascending: true })

      if (error) throw error
      return data as TenantFaq[]
    },
  })
}

export function useFAQCategories() {
  const { data: faqs = [] } = useFAQs()
  const categories = Array.from(
    new Set(faqs.map((f) => f.category).filter((c): c is string => c !== null && c !== ''))
  ).sort()
  return categories
}

export function useCreateFAQ() {
  const qc = useQueryClient()
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useMutation({
    mutationFn: async (input: CreateFaqInput) => {
      const { data, error } = await supabase
        .from('tenant_faqs')
        .insert({ ...input, tenant_id: tenantId! })
        .select()
        .single()

      if (error) throw error
      return data as TenantFaq
    },
    onSuccess: async () => {
      qc.invalidateQueries({ queryKey: ['tenant_faqs', tenantId] })
      await triggerRevalidation('tenant-faqs')
    },
  })
}

export function useUpdateFAQ() {
  const qc = useQueryClient()
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useMutation({
    mutationFn: async ({ id, ...update }: UpdateFaqInput) => {
      const { data, error } = await supabase
        .from('tenant_faqs')
        .update(update)
        .eq('id', id)
        .select()
        .single()

      if (error) throw error
      return data as TenantFaq
    },
    onSuccess: async () => {
      qc.invalidateQueries({ queryKey: ['tenant_faqs', tenantId] })
      await triggerRevalidation('tenant-faqs')
    },
  })
}

export function useDeleteFAQ() {
  const qc = useQueryClient()
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useMutation({
    mutationFn: async (id: string) => {
      const { error } = await supabase.from('tenant_faqs').delete().eq('id', id)
      if (error) throw error
    },
    onSuccess: async () => {
      qc.invalidateQueries({ queryKey: ['tenant_faqs', tenantId] })
      await triggerRevalidation('tenant-faqs')
    },
  })
}

export function useReorderFAQs() {
  const qc = useQueryClient()
  const tenantId = useAuthStore((s) => s.currentTenant?.id)

  return useMutation({
    mutationFn: async (updates: Array<{ id: string; display_order: number }>) => {
      const promises = updates.map(({ id, display_order }) =>
        supabase.from('tenant_faqs').update({ display_order }).eq('id', id)
      )
      const results = await Promise.all(promises)
      const failed = results.find((r) => r.error)
      if (failed?.error) throw failed.error
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: ['tenant_faqs', tenantId] }),
  })
}
